import { useMemo, useState } from 'react';
import useCartFromAPI from './apiLegacy/useCartFromAPI';
import useEventFromAPI from './apiLegacy/useEventFromAPI';
import useMemberFromAPI from './apiLegacy/useMemberFromAPI';
import useRegistrantClassFromAPI from './apiLegacy/useRegistrantClassFromAPI';
import useMemberEventSessions from './useMemberEventSessions';
import useViewer from './useViewer';

/**
 * Filter out sessions that have committees attached to them that the user
 * is not part of
 */
function filterSessionsByViewerCommittees(sessions, member) {
    const memberCommitteeIDs = member.active_committees_full.map(c => c.committee_id);
    const filteredSessions = sessions.filter(session => {
        // Filter our sessions have committees attached that the user is not a part of
        if (session.committees === null || session.committees.length === 0) {
            return true;
        }

        return session.committees.filter(id => memberCommitteeIDs.includes(id)).length > 0;
    });
    return filteredSessions;
}

const useEventRegistrationData = ({ eventCode, memberId }) => {
    const { viewer } = useViewer();
    const { member, refetch: refetchMember } = useMemberFromAPI({ id: memberId });
    const { event, refetch: refetchEvent } = useEventFromAPI({ code: eventCode });
    const { cart, refetch: refetchCart } = useCartFromAPI({ viewerId: viewer?.recordId });
    const { registrantClass, refetch: refetchRegistrantClass } = useRegistrantClassFromAPI({
        memberId,
        cartId: cart?.id,
        eventId: event?.id,
    });

    const { memberEventSessions } = useMemberEventSessions({
        eventCode,
        memberId,
    });

    const optionalSessions = useMemo(() => {
        if (!event || !member) {
            return;
        }

        const nonFullProgramSessions = event.functions.filter(session => session.code !== 'FP');
        const filteredByCommittee = filterSessionsByViewerCommittees(nonFullProgramSessions, member);
        return filteredByCommittee;
    }, [event, member]);

    if (
        !cart ||
        !event ||
        !member ||
        !memberEventSessions ||
        !optionalSessions ||
        !registrantClass ||
        typeof viewer === 'undefined'
    ) {
        return null;
    }

    return {
        cart,
        event,
        member,
        memberEventSessions,
        optionalSessions,
        registrantClass,
        viewer,
        refetchCart,
        refetchEvent,
        refetchMember,
        refetchRegistrantClass,
    };
};

export default useEventRegistrationData;
